import { useCallback, useEffect, useState } from "react";
import { Absent, ApiError, Unauthenticated } from "./api";
import { useSession } from "./session";

/**
 * One fetch, four outcomes, and the point is that they are four and not two.
 *
 *   loading   — asking
 *   ready     — here it is
 *   absent    — nothing here *for you*. The server answers 404 rather than 403
 *               on an unpermitted read so that an error cannot distinguish
 *               "exists but not yours" from "does not exist". This state exists
 *               so the UI honours that instead of flattening it into a failure.
 *   failed    — we could not ask. Never rendered as emptiness.
 *
 * A 401 is neither of the last two: it means the session went away, so it is
 * handed to the session provider and the shell takes over.
 */

export type Resource<T> =
  | { state: "loading" }
  | { state: "ready"; data: T }
  | { state: "absent" }
  | { state: "failed"; message: string; correlationId?: string };

export function useResource<T>(
  fetcher: () => Promise<T>,
  deps: ReadonlyArray<unknown>,
): { resource: Resource<T>; reload: () => void } {
  const { expired } = useSession();
  const [resource, setResource] = useState<Resource<T>>({ state: "loading" });
  const [nonce, setNonce] = useState(0);

  const reload = useCallback(() => setNonce((n) => n + 1), []);

  useEffect(() => {
    let live = true;
    setResource({ state: "loading" });

    fetcher()
      .then((data) => {
        if (live) setResource({ state: "ready", data });
      })
      .catch((error: unknown) => {
        if (!live) return;

        if (error instanceof Unauthenticated) {
          expired();
          return;
        }
        if (error instanceof Absent) {
          setResource({ state: "absent" });
          return;
        }
        if (error instanceof ApiError) {
          setResource({
            state: "failed",
            message: error.message,
            correlationId: error.correlationId,
          });
          return;
        }
        setResource({
          state: "failed",
          message: "The platform could not be reached.",
        });
      });

    return () => {
      live = false;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [...deps, nonce]);

  return { resource, reload };
}

/**
 * An action the user takes, with its own states.
 *
 * Kept separate from `useResource` because an action that fails must not blank
 * the screen behind it: the register stays readable and the refusal appears
 * beside the control that caused it.
 */
export function useAction<Args extends unknown[]>(
  run: (...args: Args) => Promise<void>,
) {
  const { expired } = useSession();
  const [pending, setPending] = useState(false);
  const [refusal, setRefusal] = useState<{ message: string; correlationId?: string } | null>(null);

  const invoke = useCallback(
    async (...args: Args): Promise<boolean> => {
      setPending(true);
      setRefusal(null);
      try {
        await run(...args);
        return true;
      } catch (error) {
        if (error instanceof Unauthenticated) {
          expired();
          return false;
        }
        if (error instanceof ApiError) {
          setRefusal({ message: error.message, correlationId: error.correlationId });
          return false;
        }
        setRefusal({ message: "That did not reach the platform." });
        return false;
      } finally {
        setPending(false);
      }
    },
    [run, expired],
  );

  return { invoke, pending, refusal, clearRefusal: () => setRefusal(null) };
}
